import React, { useMemo, useState } from 'react'
import styled from 'styled-components/macro'
import useOllieFindingAction from 'src/hooks/useOllieFindingAction'
import {
  OLLIE_ACTION_COPY,
  validateOllieReport,
} from 'src/utils/ollieActionFlows'
import {
  OLLIE_FINDING_SURFACES,
  OLLIE_REPORT_REASONS,
} from 'src/utils/ollieFindingsApi'
import { Buttons, PrimaryButton, QuietButton, Sheet } from './OllieActionSheet'

const RECHECK_REASON = 'already_sorted'
const RECHECKING_LABEL = 'Checking again…'

/**
 * "This isn't right" on the thread's finding: a reason, then thanks.
 * "Already sorted" makes Express check again first, so the send can take a
 * few seconds and the thanks says what it found.
 */
const OllieReportFinding = ({ checkId }) => {
  const finding = useMemo(() => ({ checkId }), [checkId])
  const { status, error, report } = useOllieFindingAction(finding)
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState(null)
  const [comment, setComment] = useState('')
  const [invalid, setInvalid] = useState(null)
  const [followUp, setFollowUp] = useState(null)

  if (followUp) {
    return (
      <Sheet role='status'>
        <Text>{followUp.thanks}</Text>
        {followUp.recheck?.evidenceLine && (
          <Muted>{followUp.recheck.evidenceLine}</Muted>
        )}
        {followUp.line && (
          <Muted>
            {followUp.line}{' '}
            {followUp.mailto && (
              <a href={followUp.mailto}>{OLLIE_ACTION_COPY.emailStephen}</a>
            )}
          </Muted>
        )}
      </Sheet>
    )
  }

  if (!open) {
    return (
      <ReportLink type='button' onClick={() => setOpen(true)}>
        {OLLIE_ACTION_COPY.reportLink}
      </ReportLink>
    )
  }

  const handleSend = async () => {
    const valid = validateOllieReport({ reason, comment })
    if (!valid.ok) {
      setInvalid(valid.message)
      return
    }
    setInvalid(null)
    const outcome = await report({
      reason,
      comment: comment.trim(),
      surface: OLLIE_FINDING_SURFACES.THREAD,
    })
    if (outcome.success) {
      setFollowUp(
        outcome.followUp || { thanks: 'Thanks, we will look into it.' },
      )
    }
  }

  const sending = status === 'reporting'

  return (
    <Sheet>
      <Text as='p'>
        <strong>{OLLIE_ACTION_COPY.reportTitle}</strong>
      </Text>
      <Reasons role='radiogroup'>
        {OLLIE_REPORT_REASONS.map(option => (
          <ReasonLabel key={option.id}>
            <input
              type='radio'
              name={`ollie-report-${checkId}`}
              checked={reason === option.id}
              disabled={sending}
              onChange={() => setReason(option.id)}
            />
            <span>{option.label}</span>
          </ReasonLabel>
        ))}
      </Reasons>
      <CommentBox
        rows={2}
        aria-label={OLLIE_ACTION_COPY.reportComment}
        placeholder={OLLIE_ACTION_COPY.reportComment}
        value={comment}
        disabled={sending}
        onChange={event => setComment(event.target.value)}
      />
      {(invalid || (status === 'error' && error?.message)) && (
        <ErrorText role='alert'>{invalid || error.message}</ErrorText>
      )}
      <Buttons>
        <PrimaryButton type='button' disabled={sending} onClick={handleSend}>
          {sending && reason === RECHECK_REASON
            ? RECHECKING_LABEL
            : OLLIE_ACTION_COPY.reportSend}
        </PrimaryButton>
        <QuietButton
          type='button'
          disabled={sending}
          onClick={() => setOpen(false)}
        >
          {OLLIE_ACTION_COPY.cancel}
        </QuietButton>
      </Buttons>
    </Sheet>
  )
}

const ReportLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin: 0 0 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  text-decoration: underline;
  cursor: pointer;
`

const Text = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const Muted = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.oliveGrey};

  a {
    color: ${({ theme }) => theme.colours.bbqBlack};
    font-weight: 600;
    text-decoration: underline;
  }
`

const Reasons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
`

const ReasonLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colours.bbqBlack};
  cursor: pointer;
`

const CommentBox = styled.textarea`
  width: 100%;
  margin-top: 10px;
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
  }
`

const ErrorText = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.chilliRed};
`

export default OllieReportFinding
